import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Iconify from "../../reusable/Iconify";
import { Document, Page, pdfjs } from 'react-pdf';
import { useEffect, useRef, useState } from 'react';
import Grant from "models/GrantModel";

pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

//pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const pdfToolbarButtonSx = {
  width: 34,
  height: 34,
  p: 0,
  border: 0,
  borderRadius: 1,
  color: 'white',
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255,255,255,0.08)',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.16)',
  },
  '&:disabled': {
    color: 'rgba(255,255,255,0.35)',
    cursor: 'not-allowed',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
};

const pdfCloseButtonSx = {
  ...pdfToolbarButtonSx,
  backgroundColor: 'rgba(0,0,0,0.35)',
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.6)',
  },
};

export function PdfPreviewer({ grant, grantDocumentType, onClose }) {
  const viewerRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [scale, setScale] = useState(1);
  const [viewerWidth, setViewerWidth] = useState(760);
  const pdfUrl = Grant.getViewUrlById(grant.id, grantDocumentType);
  const documentUrl = grant[`${grantDocumentType}`]

  const viewerRefWidth = viewerRef?.current?.clientWidth - 32;
  const scaledViewerWidth = viewerWidth * scale;

  useEffect(() => {
    const updateWidth = () => {
      if (!viewerRef.current) return;
      setViewerWidth(Math.max(260, Math.min(viewerRef.current.clientWidth - 32, 920)));
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    setLoaded(false);
    setLoadError(null);
    setNumPages(null);
    setPageNumber(1);
    setScale(1);
  }, [grant.id]);

  const goPrev = () => setPageNumber((page) => Math.max(1, page - 1));
  const goNext = () => setPageNumber((page) => Math.min(numPages || 1, page + 1));
  const zoomOut = () => setScale((value) => Math.max(0.7, Number((value - 0.15).toFixed(2))));
  const zoomIn = () => setScale((value) => Math.min(1.8, Number((value + 0.15).toFixed(2))));

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1040,
        height: { xs: '84vh', md: '88vh' },
        backgroundColor: '#202020',
        outline: 'none',
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          flexShrink: 0,
          px: { xs: 1, sm: 1.5 },
          py: 1,
          color: 'white',
          backgroundColor: '#333',
          borderBottom: '1px solid rgba(255,255,255,0.12)',
          flexWrap: 'wrap',
        }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            flexBasis: { xs: '100%', sm: 0 },
            fontWeight: 700,
            fontSize: 14,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Document Preview
        </Typography>


        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            component="button"
            type="button"
            onClick={goPrev}
            disabled={pageNumber <= 1}
            title="Previous page"
            sx={pdfToolbarButtonSx}
          >
            <Iconify icon="mdi:chevron-left" sx={{ width: 20, height: 20 }} />
          </Box>
          <Typography sx={{ width: 58, textAlign: 'center', fontSize: 13, fontWeight: 700 }}>
            {pageNumber}/{numPages || '-'}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={goNext}
            disabled={!numPages || pageNumber >= numPages}
            title="Next page"
            sx={pdfToolbarButtonSx}
          >
            <Iconify icon="mdi:chevron-right" sx={{ width: 20, height: 20 }} />
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box component="button" type="button" onClick={zoomOut} title="Zoom out" sx={pdfToolbarButtonSx}>
            <Iconify icon="mdi:minus" sx={{ width: 18, height: 18 }} />
          </Box>
          <Typography sx={{ width: 44, textAlign: 'center', fontSize: 13, fontWeight: 700 }}>
            {Math.round(scale * 100)}%
          </Typography>
          <Box component="button" type="button" onClick={zoomIn} title="Zoom in" sx={pdfToolbarButtonSx}>
            <Iconify icon="mdi:plus" sx={{ width: 18, height: 18 }} />
          </Box>
        </Stack>

        <Box
          component="a"
          href={documentUrl || pdfUrl}
          download={documentUrl}
          title="Download PDF"
          sx={{ ...pdfToolbarButtonSx, display: 'inline-flex', textDecoration: 'none' }}
        >
          <Iconify icon="material-symbols:download-rounded" sx={{ width: 18, height: 18 }} />
        </Box>

        <Box component="button" type="button" onClick={onClose} title="Close" sx={pdfCloseButtonSx}>
          <Iconify icon="mdi:close" sx={{ width: 22, height: 22 }} />
        </Box>
      </Stack>

      <Box
        ref={viewerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          p: { xs: 1.5, sm: 2 },
          backgroundColor: '#2a2a2a',
          display: 'flex',
          justifyContent: scaledViewerWidth > viewerRefWidth ? 'start' : 'center',
          alignItems: 'start',
        }}
      >
        <Document
          file={pdfUrl}
          loading={
            <Stack alignItems="center" justifyContent="center" sx={{ color: 'white', height: 320 }}>
              <CircularProgress size={28} sx={{ color: 'white', mb: 2 }} />
              <Typography sx={{ fontSize: 13 }}>Loading PDF...</Typography>
            </Stack>
          }
          error={
            <Stack alignItems="center" justifyContent="center" sx={{ color: 'white', height: 320 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>PDF preview failed.</Typography>
              <Typography sx={{ color: '#cfcfcf', fontSize: 13, textAlign: 'center' }}>
                The file can still be opened in a new tab from the toolbar.
              </Typography>
            </Stack>
          }
          onLoadSuccess={({ numPages: loadedPages }) => {
            setNumPages(loadedPages);
            setPageNumber(1);
            setLoaded(true);
          }}
          onLoadError={(error) => {
            setLoadError(error);
          }}
        >
          <Page
            pageNumber={pageNumber}
            width={scaledViewerWidth}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            loading={<CircularProgress size={24} sx={{ color: 'white', mt: 4 }} />}
          />
        </Document>
      </Box>
    </Box>
  );
}

const PDFModal=({open, handleClose, selectedGrant, grantDocumentType})=>{

  console.log("PDFMX", selectedGrant)

  if(!selectedGrant?.id){
    return <></>
  }

  return <Modal
    open={open}
    onClose={handleClose}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      backdropFilter: 'blur(4px)',
    }}
  >
    <PdfPreviewer grant={selectedGrant} grantDocumentType={grantDocumentType} onClose={handleClose} />
  </Modal>
}

export default PDFModal;
